import Swal from 'sweetalert2';

/**
 * Utility functions for Rastreabilidade por Lote
 */

/**
 * Format a date from ISO format to dd/mm/yyyy
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  
  // Handle both ISO strings and YYYY-MM-DD format
  // Remove timezone info and parse as local date
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(num => parseInt(num, 10));
  const date = new Date(year, month - 1, day);
  
  const dayStr = String(date.getDate()).padStart(2, '0');
  const monthStr = String(date.getMonth() + 1).padStart(2, '0');
  const yearStr = date.getFullYear();
  return `${dayStr}/${monthStr}/${yearStr}`;
};

/**
 * Format currency to BRL
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

/**
 * Calculate days difference between two dates
 */
export const daysDifference = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = d1 - d2;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Parse a date string (YYYY-MM-DD) as local date
 */
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(num => parseInt(num, 10));
  return new Date(year, month - 1, day);
};

/**
 * Get badge info for expiration date
 * @param {string} dVal - Expiration date (ISO format)
 * @param {number} xDias - Number of days threshold
 * @returns {{variant: string, text: string, days: number}}
 */
export const getExpirationBadge = (dVal, xDias = 30) => {
  if (!dVal) {
    return {variant: 'secondary', text: '—', days: null};
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expDate = parseLocalDate(dVal);
  if (!expDate) {
    return {variant: 'secondary', text: '—', days: null};
  }
  expDate.setHours(0, 0, 0, 0);
  
  const daysUntilExpiration = daysDifference(expDate, today);

  if (daysUntilExpiration < 0) {
    return {
      variant: 'danger',
      text: 'Vencido',
      days: daysUntilExpiration,
    };
  } else if (daysUntilExpiration <= xDias) {
    return {
      variant: 'warning',
      text: `Vence em ${daysUntilExpiration} dia${daysUntilExpiration !== 1 ? 's' : ''}`,
      days: daysUntilExpiration,
    };
  } else {
    return {
      variant: 'success',
      text: 'OK',
      days: daysUntilExpiration,
    };
  }
};

/**
 * Get row style for expired items (light danger/red background)
 * @param {string} dVal - Expiration date (ISO format)
 * @returns {{backgroundColor: string} | {}}
 */
export const getExpiredRowStyle = (dVal) => {
  if (!dVal) return {};
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expDate = parseLocalDate(dVal);
  if (!expDate) return {};
  expDate.setHours(0, 0, 0, 0);
  
  const daysUntilExpiration = daysDifference(expDate, today);
  
  // Return light red background if expired
  if (daysUntilExpiration < 0) {
    return {
      backgroundColor: '#f8d7da', // Light danger/red color
    };
  }
  
  return {};
};

/**
 * Filter items based on search criteria
 */
export const filterItems = (items, filters) => {
  const {
    empresasSelecionadas = [],
    periodo = [],
    lotes = '',
    tipoBusca = 'exata', // 'exata', 'contem', 'inicia'
    fornecedor = '',
    validadeProxima = false,
    xDias = 30,
    codigoProduto = '',
    descricaoProduto = '',
    numeroNfe = '',
    chaveAcesso = '',
    codigoEan = '',
  } = filters;

  const asNonEmptyStrings = (value) => {
    if (Array.isArray(value)) {
      return value.map((v) => String(v ?? '').trim()).filter(Boolean);
    }
    const str = String(value ?? '').trim();
    return str ? [str] : [];
  };

  const normalizeDigits = (value) => String(value ?? '').replace(/\D/g, '');

  let filtered = [...items];

  // Filter by empresa
  if (empresasSelecionadas.length > 0) {
    const empresaIds = empresasSelecionadas.map(emp => 
      typeof emp === 'object' ? emp.value : emp
    );
    filtered = filtered.filter(item => empresaIds.includes(item.empresaId));
  }

  // Filter by periodo - use local date parsing
  if (periodo && periodo.length === 2 && periodo[0] && periodo[1]) {
    const [startDate, endDate] = periodo;
    // Normalize to start/end of day for comparison
    const startOfDay = new Date(startDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);
    
    filtered = filtered.filter(item => {
      const emissaoDate = parseLocalDate(item.emissao);
      if (!emissaoDate) return false;
      return emissaoDate >= startOfDay && emissaoDate <= endOfDay;
    });
  }

  // If "validade próxima" is checked
  if (validadeProxima) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    filtered = filtered.filter(item => {
      if (!item.dVal) return false;
      
      const expDate = parseLocalDate(item.dVal);
      if (!expDate) return false;
      expDate.setHours(0, 0, 0, 0);
      const days = daysDifference(expDate, today);
      
      // Include expired items and items expiring within xDias
      return days <= xDias;
    });
  } else {
    // Filter by lotes (only if validade próxima is not checked)
    let lotesArray = [];
    
    if (Array.isArray(lotes)) {
      lotesArray = lotes.filter(l => l && String(l).trim());
    } else if (lotes && String(lotes).trim()) {
      lotesArray = String(lotes).split(',').map(l => l.trim()).filter(l => l);
    }
    
    if (lotesArray.length > 0) {
      filtered = filtered.filter(item => {
        return lotesArray.some(searchLote => {
          const itemLote = item.lote || '';
          
          switch (tipoBusca) {
            case 'exata':
              return itemLote.toUpperCase() === String(searchLote).toUpperCase();
            case 'contem':
              return itemLote.toUpperCase().includes(String(searchLote).toUpperCase());
            case 'inicia':
              return itemLote.toUpperCase().startsWith(String(searchLote).toUpperCase());
            default:
              return itemLote.toUpperCase() === String(searchLote).toUpperCase();
          }
          });
        });
      }

    // Filter by fornecedor (only if validade próxima is not checked)
    if (fornecedor && fornecedor.trim()) {
      const searchCnpj = fornecedor.replace(/\D/g, '');
      filtered = filtered.filter(item => {
        const itemCnpj = (item.cnpjEmitente || '').replace(/\D/g, '');
        return itemCnpj.includes(searchCnpj);
      });
    }
    
    // Filter by product code (cProd) / NCM
    {
      const searchCodes = asNonEmptyStrings(codigoProduto);
      if (searchCodes.length > 0) {
        filtered = filtered.filter((item) => {
          const itemCProd = String(item?.cProd ?? item?.codigoProduto ?? '').toUpperCase();
          const itemNcm = String(item?.ncm ?? '').toUpperCase();

          return searchCodes.some((code) => {
            const search = String(code).trim().toUpperCase();
            if (!search) return false;
            return itemCProd.includes(search) || itemNcm.includes(search);
          });
        });
      }
    }
    
    // Filter by product description
    {
      const searchDescs = asNonEmptyStrings(descricaoProduto).map((d) => d.toUpperCase());
      if (searchDescs.length > 0) {
        filtered = filtered.filter((item) => {
          const itemDesc = String(item?.xProd ?? '').toUpperCase();
          return searchDescs.some((desc) => itemDesc.includes(desc));
        });
      }
    }
    
    // Filter by NFe number
    {
      const searchNums = asNonEmptyStrings(numeroNfe).map(normalizeDigits).filter(Boolean);
      if (searchNums.length > 0) {
        filtered = filtered.filter((item) => {
          const itemNum = normalizeDigits(item?.nNf);
          return searchNums.some((num) => itemNum.includes(num));
        });
      }
    }
    
    // Filter by access key
    {
      const searchKeys = asNonEmptyStrings(chaveAcesso).map(normalizeDigits).filter(Boolean);
      if (searchKeys.length > 0) {
        filtered = filtered.filter((item) => {
          const itemChave = normalizeDigits(item?.chaveNfe);
          return searchKeys.some((chave) => itemChave.includes(chave));
        });
      }
    }
    
    // Filter by EAN code
    if (codigoEan && codigoEan.trim()) {
      const searchEan = codigoEan.trim();
      filtered = filtered.filter(item => {
        const itemEan = item.cEAN || '';
        return itemEan.includes(searchEan);
      });
    }
  }

  return filtered;
};

/**
 * Calculate summary metrics from filtered items
 */
export const calculateSummary = (items, xDias = 30) => {
  const uniqueNfes = new Set(items.map(item => item.chaveNfe));
  const uniqueFornecedores = new Set(items.map(item => item.cnpjEmitente));
  const valorTotal = items.reduce((sum, item) => {
    const valor = parseFloat(item.vProd) || 0;
    return sum + valor;
  }, 0);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const lotesAVencerSet = new Set();
  items.forEach(item => {
    if (!item.dVal) return;
    const expDate = new Date(item.dVal);
    expDate.setHours(0, 0, 0, 0);
    const days = daysDifference(expDate, today);
    if (days >= 0 && days <= xDias) {
      lotesAVencerSet.add(item.lote);
    }
  });

  return {
    nfesEncontradas: uniqueNfes.size,
    fornecedores: uniqueFornecedores.size,
    valorTotal,
    lotesAVencer: lotesAVencerSet.size,
  };
};

/**
 * Sort items based on criteria
 */
export const sortItems = (items, sortBy = 'emissao', sortOrder = 'desc') => {
  const sorted = [...items];
  
  sorted.sort((a, b) => {
    let valueA, valueB;
    
    switch (sortBy) {
      case 'validade':
        valueA = a.dVal ? new Date(a.dVal) : new Date('9999-12-31');
        valueB = b.dVal ? new Date(b.dVal) : new Date('9999-12-31');
        break;
      case 'emissao':
        valueA = new Date(a.emissao);
        valueB = new Date(b.emissao);
        break;
      case 'lote':
        valueA = a.lote || '';
        valueB = b.lote || '';
        break;
      case 'produto':
        valueA = a.xProd || '';
        valueB = b.xProd || '';
        break;
      case 'valor':
        valueA = a.vProd || 0;
        valueB = b.vProd || 0;
        break;
      default:
        return 0;
    }
    
    if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
  
  return sorted;
};

/**
 * Format CNPJ
 */
export const formatCnpj = (cnpj) => {
  if (!cnpj) return '';
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

/**
 * Escape a CSV cell value
 */
const escapeCsvCell = (value) => {
  if (value === null || value === undefined) {
    return '""';
  }
  const stringValue = String(value);
  // Normalize line endings to \n and escape embedded double quotes
  const normalized = stringValue.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const escaped = normalized.replace(/"/g, '""');
  return `"${escaped}"`;
};

/**
 * Export data to CSV
 */
export const exportToCSV = (items, filename = 'rastreabilidade-lotes.csv') => {
  if (items.length === 0) {
    Swal.fire({
      icon: 'warning',
      title: 'Nenhum dado',
      text: 'Nenhum dado para exportar',
    });
    return;
  }

  const headers = [
    'Empresa',
    'CNPJ Destinatário',
    'Chave NF-e',
    'Número NF',
    'Série',
    'Emissão',
    'CNPJ Emitente',
    'Fornecedor',
    'UF',
    'Item',
    'Produto',
    'NCM',
    'Lote',
    'Data Fabricação',
    'Data Validade',
    'Quantidade',
    'Unidade',
    'Valor Produto',
    'CFOP',
    'CST ICMS',
    'Alíquota ICMS',
    'BC ICMS',
    'Valor ICMS',
  ];

  const rows = items.map(item => [
    item.empresaNome || '',
    formatCnpj(item.cnpjDestinatario) || '',
    item.chaveNfe || '',
    item.nNf || '',
    item.serie || '',
    formatDate(item.emissao),
    formatCnpj(item.cnpjEmitente) || '',
    item.fornecedorNome || '',
    item.ufEmitente || '',
    item.nItem ?? '',
    item.xProd || '',
    item.ncm || '',
    item.lote || '',
    formatDate(item.dFab),
    formatDate(item.dVal),
    item.qCom ?? '',
    item.uCom || '',
    item.vProd ?? '',
    item.cfop || '',
    item.cstIcms || '',
    item.pIcms ?? '',
    item.vBcIcms ?? '',
    item.vIcms ?? '',
  ]);

  const csvContent = [
    headers.join(';'),
    ...rows.map(row => row.map(escapeCsvCell).join(';'))
  ].join('\n');

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
